import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import {
  btnStyles,
  modalGenericStyles,
  tableStyles,
  variables,
  genericIndexesStyles,
} from "../../../../../variables";
import PropTypes from "prop-types";
import { fetchWebSiteLandingPages } from "../../../../../api/DistributionSite/webSites/LandingPages";
import EditLandingPages from "../EditLandingPages";
import Loader from "../../../../../components/Loader";
import DeleteLandingPages from "../DeleteLandingPages";
import { DateTime } from "luxon";
import { genericRequestErrorHandler } from "../../../../../api";
import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import { statusMapping } from "../../common";
import { CustomButton } from "../../../../../components/CustomButton";

const trueFalseMapping = {
  true: "Yes",
  false: "No",
};

const styles = createUseStyles({
  ...modalGenericStyles,
  LandingPageModal: {
    ...modalGenericStyles.card,
  },
  loader: { width: "100vw" },
  tableContainer: {
    display: "grid",
    padding: variables.normal_gap,
    maxWidth: "100%",
    overflowX: "auto",
    overflowY: "hidden",
  },
  table: tableStyles,
  headRow: tableStyles.head.row,
  headCell: tableStyles.head.cell,
  bodyRow: tableStyles.body.row,
  cell: tableStyles.body.cell,
  actionCell: genericIndexesStyles.actionCell.mobile(),
  btn: btnStyles.btn,
  add: {
    padding: "0.75rem",
    textAlign: "end",
  },
  addBtn: { ...btnStyles.btn, width: "4rem" },
  [`@media ${variables.media.smallscreen}`]: {
    loader: { width: "100%" },
  },
});

const LandingPageModal = ({ websiteId }) => {
  const classes = styles();

  const [editModal, setEditModal] = useState(false);
  const [landingPage, setLandingPage] = useState(null);
  const [editId, setEditId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const { data: landingPages, isLoading } = useQuery({
    queryKey: ["LandingPagesList"],
    queryFn: async () =>
      await fetchWebSiteLandingPages({
        params: { website: websiteId },
      }),
    onError: (error) => genericRequestErrorHandler(error),
  });

  return (
    <div className={classes.LandingPageModal}>
      <div className={classes.cardHeader}>
        Landing Page
        <div className={classes.cardActions}>
          <CustomButton
            appearance="ghost"
            onClick={() => {
              setEditId(null);
              setEditModal(true);
            }}>
            <strong>Add</strong>
          </CustomButton>
        </div>
      </div>
      <div className={classes.tableContainer}>
        {isLoading ? (
          <div className={classes.loader}>
            <Loader />
          </div>
        ) : (
          <table className={classes.table}>
            <thead className={classes.thead}>
              <tr className={classes.headRow}>
                <th className={classes.headCell}>#</th>
                <th className={classes.headCell}>Id</th>
                <th className={classes.headCell}>Name</th>
                <th className={classes.headCell}>Title</th>
                <th className={classes.headCell}>Has Search Form</th>
                <th className={classes.headCell}>Page Type</th>
                <th className={classes.headCell}>Active From</th>
                <th className={classes.headCell}>Active To</th>
                <th className={classes.headCell}>Status</th>
                <th className={classes.headCell}>Actions</th>
              </tr>
            </thead>
            <tbody className={classes.tbody}>
              {_.get(landingPages, "data.results", []).map((lp, idx) => (
                <tr key={idx} className={classes.bodyRow}>
                  <td className={classes.cell}>{idx + 1}</td>
                  <td className={classes.cell}>{lp.id}</td>
                  <td className={classes.cell}>{lp.name || "N/A"}</td>
                  <td className={classes.cell}>{lp.title || "N/A"}</td>
                  <td className={classes.cell}>
                    {_.get(trueFalseMapping, _.get(lp, "has_search_form")) ||
                      "N/A"}
                  </td>
                  <td className={classes.cell}>{lp.page_type || "N/A"}</td>
                  <td className={classes.cell}>
                    {lp.active_from
                      ? DateTime.fromISO(lp.active_from).toLocaleString(
                          DateTime.DATE_MED_WITH_WEEKDAY
                        )
                      : "N/A"}
                  </td>
                  <td className={classes.cell}>
                    {lp.active_to
                      ? DateTime.fromISO(lp.active_to).toLocaleString(
                          DateTime.DATE_MED_WITH_WEEKDAY
                        )
                      : "N/A"}
                  </td>
                  <td className={classes.cell}>
                    {_.get(statusMapping, _.get(lp, "status")) || "N/A"}
                  </td>
                  <td className={classes.actionCell}>
                    <CustomButton
                      appearance="ghost"
                      onClick={() => {
                        setEditId(lp.id);
                        setEditModal(true);
                      }}>
                      <strong>Edit</strong>
                    </CustomButton>
                    <CustomButton
                      appearance="ghost"
                      onClick={() => {
                        setLandingPage(lp);
                        setDeleteId(lp.id);
                        setDeleteModal(true);
                      }}>
                      <strong>Delete</strong>
                    </CustomButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editModal && (
        <EditLandingPages
          id={editId}
          websiteId={websiteId}
          onClose={() => {
            setEditModal(false);
            setEditId(null);
          }}
        />
      )}
      {deleteModal && (
        <DeleteLandingPages
          id={deleteId}
          data={landingPage}
          onClose={() => {
            setDeleteModal(false);
          }}
        />
      )}
    </div>
  );
};
LandingPageModal.propTypes = {
  websiteId: PropTypes.number.isRequired,
};
export default LandingPageModal;
